import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatErr, STATIC_BASE } from "@/lib/api";
import { formatPrice, currencyFor } from "@/lib/currency";
import DualPrice from "@/components/DualPrice";
import { TID } from "@/constants/testIds";
import { Minus, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";

const resolveImg = (img) => {
    if (!img) return "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&q=80";
    if (img.startsWith("http")) return img;
    return `${STATIC_BASE}/${img}`;
};

export default function Cart() {
    const { items, remove, setQty, subtotal, discount, total, isBundled, clear } = useCart();
    const { user } = useAuth();
    const { t, lang } = useI18n();
    const [loading, setLoading] = useState(false);
    const currency = currencyFor(lang);
    const shippingEur = total >= 120 ? 0 : 12;

    const handleCheckout = async () => {
        if (items.length === 0) return;
        setLoading(true);
        try {
            const { data } = await api.post("/checkout/session", {
                items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
                origin_url: window.location.origin,
                email: user?.email,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatErr(err));
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="min-h-[70vh] flex flex-col items-center justify-center px-6 text-center">
                <p className="text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4">— {t("nav.cart")}</p>
                <h1 className="font-serif text-5xl lg:text-6xl text-bone-100 tracking-tight mb-6">{t("cart.title")}</h1>
                <p className="text-bone-300 font-light max-w-md mb-10">{t("cart.empty")}</p>
                <Link
                    to="/shop"
                    className="border border-brass-400 text-brass-400 hover:bg-brass-400 hover:text-ink-900 transition-colors duration-500 px-8 py-3.5 text-[11px] tracking-[0.28em] uppercase"
                >
                    {t("cart.continue")}
                </Link>
            </div>
        );
    }

    return (
        <div className="pt-12 pb-20">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <p className="text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-4">— {t("nav.cart")}</p>
                <h1 className="font-serif text-5xl lg:text-6xl text-bone-100 tracking-tight leading-none mb-16">
                    {t("cart.title")}
                </h1>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-8">
                        <div className="hidden md:grid grid-cols-12 gap-4 pb-4 border-b border-white/10 text-[10px] tracking-[0.24em] uppercase text-bone-300">
                            <div className="col-span-6">{t("cart.item")}</div>
                            <div className="col-span-3 text-center">{t("cart.qty")}</div>
                            <div className="col-span-3 text-right">{t("cart.total")}</div>
                        </div>
                        {items.map((item) => (
                            <div key={item.slug} className="grid grid-cols-12 gap-4 py-8 border-b border-white/5 items-center">
                                <div className="col-span-12 md:col-span-6 flex gap-5">
                                    <div className="w-24 h-28 bg-ink-800 overflow-hidden shrink-0 relative">
                                        <img src={resolveImg(item.image)} alt="" className="w-full h-full object-cover" />
                                        {isBundled(item.slug) && (
                                            <span className="absolute top-1.5 left-1.5 bg-brass-400 text-ink-900 text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 flex items-center gap-1 leading-none">
                                                <Sparkles className="w-2.5 h-2.5" strokeWidth={2} /> Pair
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex flex-col justify-between py-1">
                                        <Link to={`/product/${item.slug}`} className="font-serif text-xl text-bone-100 hover:text-brass-400 transition-colors leading-tight">
                                            {item.name}
                                        </Link>
                                        {isBundled(item.slug) && (
                                            <span className="text-[10px] tracking-[0.24em] uppercase text-brass-400 mt-1">— Bundle · 10% off</span>
                                        )}
                                        <button
                                            data-testid={TID.cartRemove(item.slug)}
                                            onClick={() => remove(item.slug)}
                                            className="text-[10px] tracking-[0.24em] uppercase text-bone-300 hover:text-brass-400 transition-colors flex items-center gap-1 self-start mt-2"
                                        >
                                            <X className="w-3 h-3" /> {t("cart.remove")}
                                        </button>
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-3 flex md:justify-center">
                                    <div className="flex items-center border border-white/15">
                                        <button onClick={() => setQty(item.slug, item.quantity - 1)} className="w-9 h-10 flex items-center justify-center text-bone-100/70 hover:text-brass-400">
                                            <Minus className="w-3 h-3" />
                                        </button>
                                        <span data-testid={TID.cartQty(item.slug)} className="w-9 text-center text-bone-100 tabular-nums text-sm">{item.quantity}</span>
                                        <button onClick={() => setQty(item.slug, item.quantity + 1)} className="w-9 h-10 flex items-center justify-center text-bone-100/70 hover:text-brass-400">
                                            <Plus className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                                <div className="col-span-6 md:col-span-3 flex md:justify-end">
                                    <DualPrice amount={item.price * item.quantity} size="lg" />
                                </div>
                            </div>
                        ))}
                        <button
                            data-testid={TID.cartClear}
                            onClick={clear}
                            className="mt-8 text-[10px] tracking-[0.24em] uppercase text-bone-300 hover:text-brass-400 transition-colors"
                        >
                            Clear cart
                        </button>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="bg-ink-800/60 p-8 border border-white/5">
                            <p className="text-[11px] tracking-[0.32em] uppercase text-brass-400 mb-6">— Summary</p>
                            <div className="flex justify-between text-sm text-bone-300 mb-4">
                                <span>{t("cart.subtotal")}</span>
                                <span className="text-bone-100 tabular-nums">{formatPrice(subtotal, lang)}</span>
                            </div>
                            {discount > 0 && (
                                <div data-testid="cart-bundle-discount" className="flex justify-between text-sm mb-4">
                                    <span className="text-brass-400 flex items-center gap-2">
                                        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                                        Bundle discount · 10%
                                    </span>
                                    <span className="text-brass-400 tabular-nums">− {formatPrice(discount, lang)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm text-bone-300 pb-6 mb-6 border-b border-white/10">
                                <span>Shipping</span>
                                <span className="text-bone-100">{total >= 120 ? "Complimentary" : formatPrice(12, lang)}</span>
                            </div>
                            <div className="flex justify-between items-start mb-8 gap-4">
                                <span className="text-[11px] tracking-[0.28em] uppercase text-bone-100 pt-2">{t("cart.total")}</span>
                                <DualPrice amount={total + shippingEur} size="xl" />
                            </div>
                            <button
                                data-testid={TID.cartCheckout}
                                onClick={handleCheckout}
                                disabled={loading}
                                className="w-full bg-brass-400 text-ink-900 hover:bg-brass-300 disabled:opacity-60 disabled:cursor-not-allowed py-4 text-[11px] tracking-[0.28em] uppercase transition-colors"
                            >
                                {loading ? t("checkout.processing") : t("cart.checkout")}
                            </button>
                            <Link
                                to="/shop"
                                className="block text-center mt-4 text-[10px] tracking-[0.24em] uppercase text-bone-300 hover:text-brass-400 transition-colors py-3"
                            >
                                {t("cart.continue")}
                            </Link>
                            <p className="text-[10px] text-bone-300/60 mt-6 text-center leading-relaxed">
                                Secure checkout · Test card 4242 4242 4242 4242
                                <br />Card charged in EUR · you see both currencies for clarity
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
